<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, toRef } from 'vue';
import {
  FilterSchema,
  type SearchOption,
  SearchOptionType,
  type SearchOptions,
  type SearchRequest,
  SearchRequestSchema,
} from 'gpcore-grpc/gpcore/type/v1/search_pb';
import { GpBtn, GpCheckbox, GpDropdown, GpTextInput } from '@gportal/uikit';
import { create } from '@bufbuild/protobuf';
import useRouteQuery from './composables/useRouteQuery';
import useFormValidation from './composables/useFormValidation';

const props = withDefaults(
  defineProps<{
    searchOptions: SearchOptions;
    paramPrefix?: string;
  }>(),
  {
    paramPrefix: 'search',
  },
);

const emit = defineEmits<{
  search: [request: SearchRequest];
}>();

const searchOptionsRef = toRef(props, 'searchOptions');
const paramPrefixRef = toRef(props, 'paramPrefix');

const searchData = reactive<Record<string, string | number | boolean | null>>({});

// internal tracking of search request PB object
const searchRequest = computed(() => {
  const request = create(SearchRequestSchema);

  Object.entries(searchData).forEach(([key, value]) => {
    if (value == null) {
      return;
    }

    const filter = create(FilterSchema, {
      name: key,
    });

    if (typeof value === 'string') {
      filter.value.case = 'stringValue';
      filter.value.value = value as string;
    } else if (typeof value === 'number') {
      filter.value.case = 'integerValue';
      filter.value.value = BigInt(value);
    } else if (typeof value === 'boolean') {
      filter.value.case = 'booleanValue';
      filter.value.value = ['true', '1', 'yes'].includes(value.toString());
    }

    request.filters.push(filter);
  });

  return request;
});

const { getInvalidMessage, isFormValid } = useFormValidation(searchData, searchOptionsRef);

const { mapRouteQueryToSearchData, updateRoute, clearRoute } = useRouteQuery(
  searchData,
  searchOptionsRef,
  paramPrefixRef,
  searchRequest,
);

const emitSearch = () => {
  if (isFormValid.value) {
    updateRoute();
    emit('search', searchRequest.value.filters.length > 0 ? searchRequest.value : undefined);
  }
};

const clearForm = () => {
  Object.keys(searchData).forEach((key) => {
    searchData[key] = null;
  });

  clearRoute();
  emitSearch();
};

const convertDropdownOptions = (option: SearchOption): Array<{ key: string; label: string }> =>
  Object.entries(option.dropdownValues)
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

const options = computed(() => props.searchOptions.options);

const getStringValue = (key: string): string => searchData[key] as string;
const getBoolValue = (key: string): boolean | undefined => searchData[key] as boolean | undefined;

onMounted(() => {
  mapRouteQueryToSearchData();
  emitSearch();
});

onUnmounted(() => {
  clearRoute();
});
</script>

<template>
  <form @submit.prevent="emitSearch" @keyup.enter="emitSearch">
    <template v-for="option in options" :key="option.name">
      <GpTextInput
        v-if="option.type === SearchOptionType.STRING"
        class="tw-mt-4"
        :input-id="`search__${option.type}__${option.name}`"
        :model-value="getStringValue(option.name)"
        @update:model-value="(val) => (searchData[option.name] = val)"
        :label="option.label || option.name"
        :error="getInvalidMessage(option.name)"
        :hint="option.description"
        nullable
        can-be-resetted
      />

      <GpDropdown
        v-if="option.type === SearchOptionType.DROPDOWN"
        class="tw-mt-4"
        :input-id="`search__${option.type}__${option.name}`"
        :model-value="getStringValue(option.name)"
        @update:model-value="(val) => (searchData[option.name] = val)"
        placeholder="Select an option"
        filter-empty-text="No options found"
        :data="convertDropdownOptions(option)"
        :label="option.label || option.name"
        :error="getInvalidMessage(option.name)"
        :hint="option.description"
        nullable
      />

      <GpCheckbox
        class="tw-mt-4"
        v-if="option.type === SearchOptionType.BOOLEAN"
        :input-id="`search__${option.type}__${option.name}`"
        :model-value="getBoolValue(option.name)"
        @update:model-value="(val) => (searchData[option.name] = val)"
        :label="option.label || option.name"
        :error="getInvalidMessage(option.name)"
        :hint="option.description"
        nullable
      />
    </template>

    <GpBtn
      :disabled="!isFormValid"
      type="submit"
      class="tw-mt-6 tw-w-full"
      outline
      label="Search"
    />
    <GpBtn
      class="tw-mt-6 tw-w-full"
      @click="clearForm"
      btn-type="cancel"
      size="sm"
      outline
      label="Clear"
    />
  </form>
</template>
